import type { OrderLineItem, Resolution, Shop } from "../types";

type VariantOptionInfo = { name: string; value: string };
type VariantInfo = {
  inventory_quantity: number;
  inventoryPolicy?: string;
  selectedOptions?: VariantOptionInfo[];
};
type ProductInfo = { variants: VariantInfo[] };
export type VariantCollection = { products: ProductInfo[] };

export type OrderOpt = {
  orderCanBeRefunded?: boolean;
  orderCanBeExchanged?: boolean;
  orderCanBeExchangedWithCredit?: boolean;
  orderCanBeDiscounted?: boolean;
  optByLineItemId?: Record<
    string,
    {
      productCanBeRefund?: boolean;
      productCanBeExchanged?: boolean;
      productCanBeExchangedWithCredit?: boolean;
      productCanBeDiscounted?: boolean;
    }
  >;
};

type ShopExchange = {
  activated?: boolean;
  available?: boolean;
  minimalStock?: number;
  exchangeWithOtherArticles?: boolean;
};
type ShopDiscount = { activated?: boolean; available?: boolean };
type ShopPreferences = { nonReturnableItems?: string[] };
type ShopForResolutions = Shop & {
  exchange?: ShopExchange;
  discount?: ShopDiscount;
  preferences?: ShopPreferences;
};
type LineItemForResolutions = OrderLineItem & {
  product_id?: string;
  line_item_id?: string;
};

function isRealVariant(v: VariantInfo): boolean {
  return (v.selectedOptions ?? []).some((o) => o.value !== "Default Title");
}

function hasAlternativeVariants(
  collection: VariantCollection,
  minimalStock: number,
): boolean {
  return collection.products.some((p) =>
    p.variants.some(
      (v) =>
        isRealVariant(v) &&
        (v.inventoryPolicy === "CONTINUE" || v.inventory_quantity > minimalStock),
    ),
  );
}

export function getAvailableResolutions(
  shop: Shop,
  lineItem: OrderLineItem,
  variantCollection?: VariantCollection | null,
  opt?: OrderOpt | null,
): Resolution[] {
  const shopTyped = shop as ShopForResolutions;
  const lineItemTyped = lineItem as LineItemForResolutions;
  const lineItemId = lineItemTyped.line_item_id ?? "";
  const lineOpt = opt?.optByLineItemId?.[lineItemId];

  const nonReturnable: string[] = shopTyped.preferences?.nonReturnableItems ?? [];
  if (lineItemTyped.product_id && nonReturnable.includes(lineItemTyped.product_id)) {
    return [];
  }

  const available: Resolution[] = [];

  const refundAllowed =
    opt?.orderCanBeRefunded !== false && lineOpt?.productCanBeRefund !== false;
  if (refundAllowed) available.push("refund");

  const exchangeEnabled =
    shopTyped.exchange?.activated === true && shopTyped.exchange?.available === true;

  const exchangeAllowed =
    opt?.orderCanBeExchanged !== false && lineOpt?.productCanBeExchanged !== false;

  if (exchangeEnabled && exchangeAllowed) {
    const minimalStock: number = shopTyped.exchange?.minimalStock ?? 0;
    const variantsAvailable =
      !variantCollection ||
      (variantCollection.products.length > 0 &&
        hasAlternativeVariants(variantCollection, minimalStock));

    if (variantsAvailable) {
      available.push("exchange");
    }
  }

  const discountEnabled =
    shopTyped.discount?.activated === true && shopTyped.discount?.available === true;
  const discountAllowed =
    opt?.orderCanBeDiscounted !== false && lineOpt?.productCanBeDiscounted !== false;
  if (discountEnabled && discountAllowed) {
    available.push("discount");
  }

  const exchangeCreditAllowed =
    opt?.orderCanBeExchangedWithCredit !== false &&
    lineOpt?.productCanBeExchangedWithCredit !== false;
  if (
    exchangeEnabled &&
    shopTyped.exchange?.exchangeWithOtherArticles &&
    exchangeCreditAllowed
  ) {
    available.push("exchange-credit");
  }

  return available;
}
